import { useRef, useState, type DragEvent } from 'react'
import {
  Upload,
  Image as ImageIcon,
  FileArchive,
  CheckCircle2,
  AlertTriangle,
  X,
  Loader2,
  Download,
  ChevronDown,
  ChevronRight,
  Info,
} from 'lucide-react'

const API = '' // ruta relativa — el proxy de Vite reenvía a Next

interface UploadedItem {
  sku: string
  filename: string
  slot: number
  url: string
}
interface NotFoundItem {
  sku: string
  filename: string
}
interface ErrorItem {
  filename: string
  message: string
}
interface BulkResult {
  ok: true
  uploaded: number
  uploadedItems: UploadedItem[]
  notFound: NotFoundItem[]
  invalidFiles: string[]
  errors: ErrorItem[]
}

const ACCEPTED = '.jpg,.jpeg,.png,.webp,.avif,.zip'
const MAX_FILES = 500

function isZip(name: string) {
  return name.toLowerCase().endsWith('.zip')
}

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(2)} MB`
}

function downloadCsv(filename: string, header: string[], rows: string[][]) {
  const escape = (v: string) => {
    if (v.includes(',') || v.includes('"') || v.includes('\n')) {
      return `"${v.replace(/"/g, '""')}"`
    }
    return v
  }
  const content =
    [header, ...rows].map((r) => r.map(escape).join(',')).join('\n') + '\n'
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function ProductosImagenesMasivas() {
  const [files, setFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [result, setResult] = useState<BulkResult | null>(null)
  const [error, setError] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  function addFiles(list: FileList | File[] | null) {
    if (!list) return
    const incoming = Array.from(list)
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}-${f.size}`))
      const merged = [...prev]
      for (const f of incoming) {
        const key = `${f.name}-${f.size}`
        if (!seen.has(key)) {
          merged.push(f)
          seen.add(key)
        }
      }
      return merged.slice(0, MAX_FILES)
    })
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  function clearAll() {
    setFiles([])
    setResult(null)
    setError('')
    setProgress(0)
  }

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault()
    setDragOver(false)
    addFiles(e.dataTransfer.files)
  }

  function upload() {
    if (!files.length) return
    setUploading(true)
    setError('')
    setResult(null)
    setProgress(0)

    const fd = new FormData()
    for (const f of files) fd.append('files', f)

    // Usamos XHR para reportar progreso de upload
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API}/api/admin/productos/imagenes-masivas`)
    xhr.withCredentials = true

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        setProgress(Math.round((e.loaded / e.total) * 100))
      }
    }

    xhr.onload = () => {
      setUploading(false)
      let data: any = {}
      try {
        data = JSON.parse(xhr.responseText)
      } catch {
        setError('Respuesta inválida del servidor')
        return
      }
      if (xhr.status >= 200 && xhr.status < 300 && data.ok) {
        setResult(data as BulkResult)
      } else {
        setError(data?.error || `HTTP ${xhr.status}`)
      }
    }

    xhr.onerror = () => {
      setUploading(false)
      setError('Error de red durante la subida')
    }

    xhr.send(fd)
  }

  const totalSize = files.reduce((s, f) => s + f.size, 0)

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-extrabold text-ink-900">
          <Upload className="size-6 text-brand-600" />
          Carga masiva de imágenes
        </h1>
        <p className="text-sm text-ink-500">
          Sube imágenes de productos por lote. El nombre del archivo determina a qué producto se asocia.
        </p>
      </header>

      {/* Hint con instrucciones */}
      <section className="rounded-2xl border border-brand-200 bg-brand-50/60 p-4">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 size-5 shrink-0 text-brand-600" />
          <div className="text-sm leading-relaxed text-ink-700">
            <p className="font-semibold text-ink-900">¿Cómo nombrar tus archivos?</p>
            <p className="mt-1">
              El nombre de cada archivo debe ser exactamente el <strong>SKU</strong> del producto.
              Ejemplo: <code className="rounded bg-white px-1 py-0.5 font-mono text-xs">ADCPU07CLF-256.jpg</code>{' '}
              busca el producto con SKU{' '}
              <code className="rounded bg-white px-1 py-0.5 font-mono text-xs">ADCPU07CLF-256</code>{' '}
              y le asocia esa imagen como principal.
            </p>
            <p className="mt-2">
              Los sufijos <code className="rounded bg-white px-1 py-0.5 font-mono text-xs">-2</code>,{' '}
              <code className="rounded bg-white px-1 py-0.5 font-mono text-xs">-3</code>,{' '}
              <code className="rounded bg-white px-1 py-0.5 font-mono text-xs">-4</code>,{' '}
              <code className="rounded bg-white px-1 py-0.5 font-mono text-xs">-5</code> van a las columnas{' '}
              <code className="rounded bg-white px-1 py-0.5 font-mono text-xs">image2</code>..
              <code className="rounded bg-white px-1 py-0.5 font-mono text-xs">image5</code>.
            </p>
            <p className="mt-2 text-xs text-ink-500">
              Formatos aceptados: JPG, PNG, WebP, AVIF · Máx 10 MB por imagen · Hasta {MAX_FILES} archivos por lote ·
              También aceptamos un <strong>.zip</strong> con imágenes adentro.
            </p>
          </div>
        </div>
      </section>

      {/* Dropzone */}
      <section className="rounded-2xl border border-ink-200 bg-white p-5">
        <h2 className="mb-3 font-semibold text-ink-900">1. Selecciona los archivos</h2>
        <label
          htmlFor="bulk-image-input"
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-12 transition-colors ${
            dragOver
              ? 'border-brand-500 bg-brand-50'
              : 'border-ink-200 bg-ink-50 hover:border-brand-400 hover:bg-brand-50/50'
          }`}
        >
          <Upload className="size-10 text-ink-400" />
          <div className="text-center">
            <p className="font-semibold text-ink-700">
              Arrastra hasta {MAX_FILES} archivos o haz clic para seleccionar
            </p>
            <p className="mt-1 text-xs text-ink-500">
              JPG, PNG, WebP, AVIF o un .zip · El filename = SKU del producto
            </p>
          </div>
          <input
            ref={inputRef}
            id="bulk-image-input"
            type="file"
            multiple
            accept={ACCEPTED}
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files)
              e.target.value = ''
            }}
          />
        </label>

        {files.length > 0 && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <div className="text-ink-600">
                <strong className="text-ink-900">{files.length}</strong>{' '}
                {files.length === 1 ? 'archivo' : 'archivos'} ·{' '}
                <span className="text-ink-500">{formatBytes(totalSize)}</span>
              </div>
              <button
                type="button"
                onClick={clearAll}
                disabled={uploading}
                className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
              >
                Limpiar todo
              </button>
            </div>

            <div className="max-h-72 overflow-y-auto rounded-lg border border-ink-200 bg-ink-50">
              <ul className="divide-y divide-ink-100">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      {isZip(f.name) ? (
                        <FileArchive className="size-4 shrink-0 text-amber-600" />
                      ) : (
                        <ImageIcon className="size-4 shrink-0 text-brand-600" />
                      )}
                      <span className="truncate font-mono text-xs text-ink-800">{f.name}</span>
                    </div>
                    <span className="shrink-0 text-xs text-ink-500">{formatBytes(f.size)}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      disabled={uploading}
                      className="grid size-6 place-items-center rounded text-ink-400 hover:bg-ink-200 hover:text-ink-700 disabled:opacity-50"
                      title="Quitar"
                    >
                      <X className="size-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={upload}
                disabled={uploading || files.length === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {uploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                {uploading ? `Subiendo… ${progress}%` : `Subir ${files.length} archivo${files.length === 1 ? '' : 's'}`}
              </button>
            </div>

            {uploading && (
              <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
                <div
                  className="h-full bg-brand-600 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 size-4" />
            {error}
          </div>
        )}
      </section>

      {result && <ResultPanel result={result} />}
    </div>
  )
}

function ResultPanel({ result }: { result: BulkResult }) {
  return (
    <section className="rounded-2xl border border-ink-200 bg-white p-5">
      <div className="mb-4 flex items-center gap-2 border-b border-ink-100 pb-3">
        <CheckCircle2 className="size-5 text-emerald-600" />
        <h2 className="font-semibold text-ink-900">Resultado</h2>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Subidas" value={result.uploaded} color="emerald" />
        <Stat
          label="SKUs no encontrados"
          value={result.notFound.length}
          color={result.notFound.length ? 'amber' : 'ink'}
        />
        <Stat
          label="Archivos inválidos"
          value={result.invalidFiles.length}
          color={result.invalidFiles.length ? 'amber' : 'ink'}
        />
        <Stat
          label="Errores"
          value={result.errors.length}
          color={result.errors.length ? 'red' : 'ink'}
        />
      </div>

      {result.notFound.length > 0 && (
        <Collapsible
          title={`SKUs no encontrados (${result.notFound.length})`}
          variant="amber"
          downloadCsv={() =>
            downloadCsv(
              'skus-no-encontrados.csv',
              ['sku', 'filename'],
              result.notFound.map((n) => [n.sku, n.filename]),
            )
          }
        >
          <ul className="max-h-72 space-y-1 overflow-y-auto text-xs">
            {result.notFound.map((n, i) => (
              <li
                key={i}
                className="flex justify-between gap-3 rounded border border-amber-100 bg-amber-50 p-2 font-mono text-amber-800"
              >
                <span className="font-semibold">{n.sku}</span>
                <span className="text-amber-700/70">{n.filename}</span>
              </li>
            ))}
          </ul>
        </Collapsible>
      )}

      {result.invalidFiles.length > 0 && (
        <Collapsible
          title={`Archivos inválidos (${result.invalidFiles.length})`}
          variant="amber"
          downloadCsv={() =>
            downloadCsv(
              'archivos-invalidos.csv',
              ['filename'],
              result.invalidFiles.map((f) => [f]),
            )
          }
        >
          <ul className="max-h-72 space-y-1 overflow-y-auto text-xs">
            {result.invalidFiles.map((f, i) => (
              <li
                key={i}
                className="rounded border border-amber-100 bg-amber-50 p-2 font-mono text-amber-800"
              >
                {f}
              </li>
            ))}
          </ul>
        </Collapsible>
      )}

      {result.errors.length > 0 && (
        <Collapsible
          title={`Errores (${result.errors.length})`}
          variant="red"
          downloadCsv={() =>
            downloadCsv(
              'errores.csv',
              ['filename', 'message'],
              result.errors.map((e) => [e.filename, e.message]),
            )
          }
        >
          <ul className="max-h-72 space-y-1 overflow-y-auto text-xs">
            {result.errors.map((e, i) => (
              <li
                key={i}
                className="rounded border border-red-100 bg-red-50 p-2 font-mono text-red-800"
              >
                <div className="font-semibold">{e.filename}</div>
                <div className="text-red-700/80">{e.message}</div>
              </li>
            ))}
          </ul>
        </Collapsible>
      )}

      {result.uploaded > 0 && (
        <Collapsible
          title={`Subidas exitosas (${result.uploaded})`}
          variant="emerald"
          downloadCsv={() =>
            downloadCsv(
              'subidas.csv',
              ['sku', 'slot', 'filename', 'url'],
              result.uploadedItems.map((u) => [u.sku, String(u.slot), u.filename, u.url]),
            )
          }
        >
          <ul className="max-h-72 space-y-1 overflow-y-auto text-xs">
            {result.uploadedItems.map((u, i) => (
              <li
                key={i}
                className="flex items-center justify-between gap-3 rounded border border-emerald-100 bg-emerald-50 p-2 text-emerald-800"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2 font-mono">
                  <span className="font-semibold">{u.sku}</span>
                  {u.slot > 1 && (
                    <span className="rounded bg-emerald-200/70 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-emerald-900">
                      image{u.slot}
                    </span>
                  )}
                </div>
                <a
                  href={u.url}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 text-emerald-700 underline hover:text-emerald-900"
                >
                  Ver
                </a>
              </li>
            ))}
          </ul>
        </Collapsible>
      )}
    </section>
  )
}

function Stat({
  label,
  value,
  color = 'ink',
}: {
  label: string
  value: number
  color?: 'ink' | 'emerald' | 'red' | 'amber'
}) {
  const colors: Record<string, string> = {
    ink: 'text-ink-900 bg-ink-50',
    emerald: 'text-emerald-700 bg-emerald-50',
    red: 'text-red-700 bg-red-50',
    amber: 'text-amber-700 bg-amber-50',
  }
  return (
    <div className={`rounded-xl p-3 text-center ${colors[color]}`}>
      <div className="text-2xl font-extrabold">{value.toLocaleString('es-MX')}</div>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
    </div>
  )
}

function Collapsible({
  title,
  variant,
  children,
  downloadCsv,
}: {
  title: string
  variant: 'red' | 'amber' | 'emerald'
  children: React.ReactNode
  downloadCsv?: () => void
}) {
  const [open, setOpen] = useState(false)
  const styles: Record<string, string> = {
    red: 'text-red-700 hover:bg-red-50',
    amber: 'text-amber-700 hover:bg-amber-50',
    emerald: 'text-emerald-700 hover:bg-emerald-50',
  }
  return (
    <div className="mt-4 rounded-lg border border-ink-200">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`flex flex-1 items-center gap-2 text-left text-sm font-semibold ${styles[variant]} rounded px-1 py-1`}
        >
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          {title}
        </button>
        {downloadCsv && (
          <button
            type="button"
            onClick={downloadCsv}
            className="inline-flex items-center gap-1 rounded border border-ink-200 bg-white px-2 py-1 text-xs font-semibold text-ink-700 hover:bg-ink-50"
          >
            <Download className="size-3.5" />
            CSV
          </button>
        )}
      </div>
      {open && <div className="border-t border-ink-100 p-3">{children}</div>}
    </div>
  )
}

export default ProductosImagenesMasivas
